import React, { Fragment, useCallback, useEffect } from 'react';
import './DrawerV6.scss';
import Icon from '@Common/Icon';
import Button from '@Common/Button';

type DrawerProps = {
    isWide: boolean;
    isMobile?: boolean;
    title: string | React.ReactNode;
    onSaveClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    saveText?: string;
    isSaveDisabled?: boolean;
    onCancelClick?: () => void;
    cancelText?: string;
    menuButtons?: React.ReactElement[];
    currentDrawerKey?: string | number | null;
    formButtons?: React.ReactElement[];
    customFooter?: React.ReactNode;
    children?: React.ReactNode;
};

const DrawerV6 = ({
    isWide,
    isMobile,
    title,
    onSaveClick,
    saveText,
    isSaveDisabled,
    onCancelClick,
    cancelText,
    menuButtons,
    currentDrawerKey,
    formButtons,
    customFooter,
    children,
}: DrawerProps) => {
    const cleanUp = useCallback(() => {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
    }, []);

    const openDrawer = useCallback(() => {
        document.body.classList.add('modal-open');
        document.body.style.overflow = 'hidden';
    }, []);

    const closeClick = useCallback(() => {
        cleanUp();
        onCancelClick?.();
    }, [cleanUp, onCancelClick]);

    const saveClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            return onSaveClick?.(event);
        },
        [onSaveClick]
    );

    useEffect(() => {
        openDrawer();
        const escFunction = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeClick();
            }
        };
        document.addEventListener('keydown', escFunction, false);
        return () => {
            cleanUp();
            document.removeEventListener('keydown', escFunction, false);
        };
    }, [closeClick, cleanUp, openDrawer]);

    const wideCheck = isWide ? ' drawer-wide' : '';
    const drawerClassName = `drawerv6 active ${wideCheck}`.trim();

    let buttonCount = 0;
    if (onSaveClick) buttonCount++;
    if (formButtons) buttonCount += formButtons.length;
    if (onCancelClick) buttonCount++;

    const buttonWrapClass = `button-wrap`;

    return (
        <div className={drawerClassName}>
            <div className={`inner${buttonCount === 3 ? ' three-buttons' : ''}`}>
                <header>
                    <div className='drawer-title'>
                        {React.isValidElement(title) ? <div>{title}</div> : <div className='drawer-title-text'>{title}</div>}
                    </div>
                    <div className='drawer-span menuButtons'>
                        {menuButtons && (
                            <Fragment>
                                {menuButtons.map((menuButton) => {
                                    if (!currentDrawerKey || menuButton.key !== currentDrawerKey) {
                                        return menuButton;
                                    }
                                    return null;
                                })}
                            </Fragment>
                        )}
                        <div className='menuButtonGroup'>
                            <button name='close' type='button' className='btn' onClick={closeClick} aria-label='close popup'>
                                <Icon name='times' />
                            </button>
                        </div>
                    </div>
                </header>
                <section>
                    <div className='container'>
                        <br />
                        {children}
                    </div>
                </section>
            </div>
            <footer className={buttonCount === 3 ? 'three-buttons' : undefined}>
                {customFooter ? (
                    customFooter
                ) : (
                    <div className={buttonWrapClass}>
                        {onSaveClick && (
                            <Button
                                type='button'
                                bootstrapClass='drawer-button btn btn-primary button--save'
                                onClick={saveClick}
                                text={saveText ?? 'Save'}
                                isDisabled={isSaveDisabled}
                            />
                        )}
                        {formButtons?.map((formButton) => formButton) ?? null}
                        <Button
                            type='button'
                            bootstrapClass='drawer-button btn btn-light button--cancel'
                            onClick={closeClick}
                            text={cancelText ?? 'Cancel'}
                        />
                    </div>
                )}
            </footer>
        </div>
    );
};

export default DrawerV6;
